<template>
  <div class="pl-4">
    <MobileSearchSection
      title="Your registrations"
      :loading="privateLoading"
      :total="privateTotal"
      empty-label="No matching registrations or folders"
      class="mb-3"
      @view-all="$emit('view-all', 'private')"
    >
      <Table>
        <Table.Row
          v-for="item in privateItems"
          :key="item.id"
          class="pr-4"
          :class="itemDimClass(item)"
          @row-click="goToItem(item)"
        >
          <template #media>
            <div class="size-9">
              <Thumbnail
                :type="itemThumbType(item)"
                :src="itemThumbSrc(item) ?? undefined"
              />
            </div>
          </template>
          <template #mobile>
            <div class="min-w-0 flex-1 pl-2.5">
              <div
                v-truncate
                class="min-w-0 text-[14px] leading-[21px] text-gray-800"
              >
                <div data-truncate-target>{{ item.name }}</div>
              </div>
              <div
                class="mt-0.5 flex min-w-0 items-center gap-1.5 text-[13px] leading-[18px] text-[#5a616b]"
              >
                <div v-truncate class="min-w-0 flex-1">
                  <div data-truncate-target>
                    {{ formatPath(item.parentPath) }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Table.Row>
      </Table>
    </MobileSearchSection>

    <MobileSearchSection
      title="Public registrations"
      :loading="publicLoading"
      :total="publicTotal"
      empty-label="No matching public registrations"
      @view-all="$emit('view-all', 'public')"
    >
      <Table>
        <RouterLink
          v-for="file in publicFiles"
          :key="file.id"
          :to="`/file/${file.id}`"
          class="block"
          @click.left.exact="store.close()"
        >
          <Table.Row class="pr-4">
            <template #media>
              <div class="size-9">
                <Thumbnail
                  :type="fileThumbType(file)"
                  :src="fileThumbSrc(file) ?? undefined"
                />
              </div>
            </template>
            <template #mobile>
              <div class="min-w-0 flex-1 pl-2.5">
                <div
                  v-truncate
                  class="min-w-0 text-[14px] leading-[21px] text-gray-800"
                >
                  <div data-truncate-target>{{ file.name }}</div>
                </div>
                <div
                  class="mt-0.5 flex min-w-0 items-center gap-1.5 text-[13px] leading-[18px] text-[#5a616b]"
                >
                  <DacrMarkIcon class="size-[14px] shrink-0 text-gray-800" />
                  <div v-truncate class="min-w-0 flex-1">
                    <div data-truncate-target>
                      {{ formatTimestamp(file.timestamp) }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Table.Row>
        </RouterLink>
      </Table>
    </MobileSearchSection>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { Table, Thumbnail } from '@dacr/library'
import { DacrMarkIcon } from '@dacr/icons'
import {
  useSearchItemsQuery,
  useSearchFilesQuery,
  type Item,
  type SearchFile,
} from '@/shared/graphql/legacy/generated'
import { useMobileSearchStore } from '@/features/search/store'
import { useSearchNavigation } from '@/features/search/composables/useSearchNavigation'
import {
  itemThumbType,
  itemThumbSrc,
  fileThumbType,
  fileThumbSrc,
  formatPath,
  itemDimClass,
} from '@/features/search/utils'
import { formatTimestamp } from '@/shared/utils/datetime'
import MobileSearchSection from '@/features/search/components/mobile/MobileSearchSection.vue'

defineEmits<{ 'view-all': [view: 'private' | 'public'] }>()

const store = useMobileSearchStore()
const { goToItem } = useSearchNavigation()

const variables = computed(() => ({ query: store.query }))

const { result: privateResult } = useSearchItemsQuery(variables)
const { result: publicResult } = useSearchFilesQuery(variables)

const privateLoading = computed(() => !privateResult.value)
const publicLoading = computed(() => !publicResult.value)

const privateTotal = computed(() => privateResult.value?.searchItems.total)
const publicTotal = computed(() => publicResult.value?.searchFiles.total)

const privateItems = computed<Item[]>(
  () =>
    (privateResult.value?.searchItems.files as Item[] | undefined)?.slice(
      0,
      3
    ) ?? []
)
const publicFiles = computed<SearchFile[]>(
  () =>
    (publicResult.value?.searchFiles.files as SearchFile[] | undefined)?.slice(
      0,
      3
    ) ?? []
)
</script>
